/* eslint-disable react-hooks/exhaustive-deps */
import {
  ChangeEvent,
  useEffect,
  useRef,
} from 'react';
import useFilterStore from '../streetview/filterStore';
import { FrontendFilterValue } from '../../types/filters.type';
import calculatePercent from '../../utils/helpers';
import FilterDescription from '../streetview/filterDescription';

export default function DualRangeSlider({
  title,
  label,
  labelId,
  filterValues,
  description,
  featureFlag,
}: Readonly<{
  title: string,
  label: string,
  labelId: string,
  filterValues: FrontendFilterValue[],
  description?: string | null,
  featureFlag?: string | null,
}>) {
  const defaultMinimum = Number(filterValues[0].id);
  const defaultMaximum = Number(filterValues[filterValues.length - 1].id);

  const selectedFilters = useFilterStore((state) => state.selectedFilters);
  const setSelectedFilters = useFilterStore((state) => state.setSelectedFilters);
  const defaultRange = [{ id: defaultMinimum.toString(), displayName: defaultMinimum.toString() },
    { id: defaultMaximum.toString(), displayName: defaultMaximum.toString() }];

  const rangeValues = selectedFilters[title]?.[labelId]?.selectedValues || defaultRange;

  const range = [
    rangeValues?.[0].id ? Number(rangeValues[0].id) : defaultMinimum,
    rangeValues?.[1].id ? Number(rangeValues[1].id) : defaultMaximum,
  ].sort((a, b) => a - b) as [number, number];
  const isOverlapping = range[0] === range[1];

  const rangeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (rangeRef.current) {
      const minPercent = calculatePercent(range[0], defaultMinimum, defaultMaximum);
      const maxPercent = calculatePercent(range[1], defaultMinimum, defaultMaximum);

      rangeRef.current.style.left = `${minPercent}%`;
      rangeRef.current.style.width = `${maxPercent - minPercent}%`;
    }
  }, [range, title, labelId]);

  const handleInputChange = (newRange: [number, number]) => {
    setSelectedFilters(title, labelId, label, [{ displayName: newRange[0].toString(), id: newRange[0].toString() },
      { id: newRange[1].toString(), displayName: newRange[1].toString() }]);
  };

  return (
    <fieldset
      id={`fieldset-${labelId}`}
      className="h-fit flex space-y-2 flex-col justify-center min-h-10"
      aria-describedby={`${labelId}-description ${labelId}-range-summary`}
    >
      <legend id={`${labelId}-label`} className="input-label">{label}</legend>
      <FilterDescription ariaId={`${labelId}-description`} description={description} featureFlag={featureFlag} />
      <div id="slider-wrapper" className="w-full py-4">
        <div className="relative w-60">
          <div className="absolute rounded bg-gray-300  h-1 w-full" />
          <div
            ref={rangeRef}
            className="absolute rounded bg-primary h-1"
          />
          <div className="absolute text-secondary text-xs mt-5 left-1">{range[0]}</div>
          <div className="absolute text-secondary text-xs mt-5 -right-1">{range[1]}</div>
        </div>
        <div className="min-h-10 relative">
          <input
            type="range"
            id={`${labelId}-min-value`}
            aria-labelledby={`${labelId}-label ${labelId}-min-value-text`}
            min={defaultMinimum}
            max={defaultMaximum}
            value={range[0]}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              const targetValue = event.target.value;
              const value = Math.min(+targetValue, range[1]);
              handleInputChange([value, range[1]]);
            }}
            className={`absolute pointer-events-none h-0 w-60 outline-none appearance-none ${isOverlapping ? 'dual-range-overlap-min' : ''}`}
          />
          <span id={`${labelId}-min-value-text`} className="sr-only">
            Minimum value:
            {range[0]}
          </span>
          <input
            type="range"
            id={`${labelId}-max-value`}
            aria-labelledby={`${labelId}-label ${labelId}-max-value-text`}
            min={defaultMinimum}
            max={defaultMaximum}
            value={range[1]}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              const targetValue = event.target.value;
              const value = Math.max(+targetValue, range[0]);
              handleInputChange([range[0], value]);
            }}
            className={`absolute pointer-events-none h-0 w-60 outline-none appearance-none ${isOverlapping ? 'dual-range-overlap-max' : ''}`}
          />
          <span id={`${labelId}-max-value-text`} className="sr-only">
            Maximum value:
            {range[1]}
          </span>
        </div>
      </div>
    </fieldset>
  );
}
